import * as React from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Image,
  makeStyles,
  shorthands,
  Spinner,
  Text,
  Title2,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useAcceptInvitationMutation } from '@foundershub/api-client';
import { InputField, MessageBar, TextLink } from '@foundershub/components';
import { LinkedInButton, useTitle } from '../components';
import workspaceImage from '../media/workspace.webp';
import { getErrorMessages, getFieldError, getValidationState } from '../modules/errors';

export default function Layout() {
  const intl = useIntl();
  const history = useHistory();
  const [email, setEmail] = React.useState<string>('');
  const [acceptInvitation, { isLoading, isSuccess, isError, error }] = useAcceptInvitationMutation();
  const classes = useStyles();

  useTitle(intl.formatMessage(pageTitle));

  function handleCheckEmail(event: React.FormEvent) {
    event.preventDefault();
    const search = new URLSearchParams(history.location.search);
    acceptInvitation({ verification_code: search.get('uid') || '', email });
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <p>
          {intl.formatMessage(
            {
              defaultMessage: 'Already a Microsoft for Startups Founders Hub user? <link>Sign in</link>',
              description: '{Locked="Microsoft for Startups Founders Hub"}, already a foundershub user login link',
            },
            {
              link: function Link(str) {
                // @ts-ignore formatjs/formatjs#3707
                return (<TextLink href="/login">{str}</TextLink>) as string;
              },
            }
          )}
        </p>

        <div>
          <Title2 as="h1" block>
            {intl.formatMessage(joinTitle)}
          </Title2>

          <p>{intl.formatMessage(joinDescription)}</p>

          <form className={classes.form} onSubmit={handleCheckEmail}>
            <InputField
              contentAfter={
                isSuccess ? (
                  <CheckmarkCircleFilled
                    aria-label={intl.formatMessage(successLabel)}
                    fontSize={20}
                    color={tokens.colorPaletteGreenForeground1}
                  />
                ) : null
              }
              disabled={isSuccess}
              validationMessage={getFieldError(error, 'email', intl)}
              validationState={getValidationState(error, 'email')}
              label={intl.formatMessage(confirmEmailLabel)}
              type="email"
              onChange={(event, data) => {
                setEmail(data.value);
              }}
              required
            />

            {isError && error ? (
              <MessageBar type="danger">
                {getErrorMessages(error, intl).map((message, i) => (
                  <Text key={i}>{message}</Text>
                ))}
              </MessageBar>
            ) : null}

            {isSuccess ? (
              <LinkedInButton loginUri="/auth/linkedin-login" />
            ) : (
              <div className={classes.buttons}>
                {isLoading ? <Spinner /> : null}
                <Button type="submit" appearance="primary">
                  {intl.formatMessage(verifyEmailLabel)}
                </Button>
              </div>
            )}
          </form>
        </div>

        <p>
          <>
            {intl.formatMessage(
              {
                defaultMessage:
                  'By submitting this application, you agree that you have read and acknowledge the <link>Microsoft Privacy Statement</link> and give permission to Microsoft to collect your data and use your contact information to provide you with updates regarding your application to Microsoft for Startups Founders Hub and related resources.',
                description:
                  '{Locked="Microsoft for Startups Founders Hub"}, privacy statement acknowledgement on submission of application text',
              },
              {
                link: function Link(str) {
                  // @ts-ignore formatjs/formatjs#3707
                  return (
                    <TextLink openNewTab href="https://go.microsoft.com/fwlink/?LinkId=521839">
                      {str}
                    </TextLink>
                  ) as string;
                },
              }
            )}
          </>
        </p>
      </div>

      <aside className={classes.aside}>
        <div className={classes.asideContent}>
          <Title2 as="h2" block>
            {intl.formatMessage(title)}
          </Title2>

          <p>{intl.formatMessage(description)}</p>
          <p>
            <TextLink openNewTab href="https://foundershubsupportcenter.powerappsportals.com/">
              {intl.formatMessage(exploreMoreLabel)}
            </TextLink>
          </p>

          <Accordion className={classes.accordion}>
            <AccordionItem value="1">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(whatIsFhText)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'Microsoft for Startups Founders Hub is a platform for all founders at any stage to reach their next milestone through personalized technical tools and business resources. <link>Learn more about the specifics</link>',
                    description: '{Locked="Microsoft for Startups Founders Hub"}, signup page foundershub intro text',
                  },
                  {
                    link: function Link(str) {
                      // @ts-ignore formatjs/formatjs#3707
                      return (
                        <TextLink openNewTab href="https://foundershubsupportcenter.powerappsportals.com/">
                          {str}
                        </TextLink>
                      ) as string;
                    },
                  }
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="2">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(whoQualifiesText)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'All startup founders, from those who just had inspiration spark to those scaling their business are welcome to join Microsoft for Startups Founders Hub. Learn more about our qualification criteria <link>here</link>.',
                    description: '{Locked="Microsoft for Startups Founders Hub"}, welcome signup page text',
                  },
                  {
                    link: function Link(str) {
                      // @ts-ignore formatjs/formatjs#3707
                      return (
                        <TextLink openNewTab href="https://foundershubsupportcenter.powerappsportals.com/">
                          {str}
                        </TextLink>
                      ) as string;
                    },
                  }
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="3">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(howLongText)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  defineMessage({
                    defaultMessage:
                      'Once you begin, you should expect to take just about 10 minutes to complete your application, so you can get right back to working on your business.',
                    description: 'application length of time summary',
                  })
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="4">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(whatHappensAfterText)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  defineMessage({
                    defaultMessage:
                      'We review all applications to evaluate whether our acceptance criteria is met and will reach back out when a final decision has been made. We accept new users on a rolling basis and aim to provide a decision in 3-5 business days.',
                    description: 'post application review summary text',
                  })
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="5">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(whyLinkedInAccountText)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  defineMessage({
                    defaultMessage:
                      'We believe that startup founders are only as powerful as their network. With LinkedIn, we can activate potential mentors, advisors, and investors to help you reach your next milestone.',
                    description: 'linkedin requirement explanation text',
                  })
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem value="6">
              <AccordionHeader className={classes.accordion} size="large">
                {intl.formatMessage(whatIfNoLinkedIn)}
              </AccordionHeader>
              <AccordionPanel>
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'If you are having trouble accessing your LinkedIn account, please reach out to <link1>LinkedIn Support</link1> for guidance. For new users, you can easily sign up for an account at <link2>LinkedIn.com</link2>. At this time, we cannot accept applications to Microsoft for Startups Founders Hub without a LinkedIn account.',
                    description: '{Locked="Microsoft for Startups Founders Hub"}, linkedin support text and links',
                  },
                  {
                    link1: function Link1(str) {
                      // @ts-ignore formatjs/formatjs#3707
                      return (
                        <TextLink openNewTab href="https://linkedin.com/help/linkedin">
                          {str}
                        </TextLink>
                      ) as string;
                    },
                    link2: function Link2(str) {
                      // @ts-ignore formatjs/formatjs#3707
                      return (
                        <TextLink openNewTab href="https://linkedin.com">
                          {str}
                        </TextLink>
                      ) as string;
                    },
                  }
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          <Image src={workspaceImage} alt="" fit="contain" className={classes.asideImage} />
        </div>
      </aside>
    </div>
  );
}

const exploreMoreLabel = defineMessage({
  defaultMessage: 'Explore more FAQ or reach out to us here.',
  description: 'link for FAQ',
});
const whoQualifiesText = defineMessage({
  defaultMessage: 'Who qualifies for Microsoft for Startups Founders Hub?',
  description: '{Locked="Microsoft for Startups Founders Hub"}, who qualifies for founders hub header text',
});
const whatIsFhText = defineMessage({
  defaultMessage: 'What is Microsoft for Startups Founders Hub?',
  description: '{Locked="Microsoft for Startups Founders Hub"}, what is foundershub title text',
});
const howLongText = defineMessage({
  defaultMessage: 'How long does the application take?',
  description: 'how long does the application take title',
});
const whatHappensAfterText = defineMessage({
  defaultMessage: 'What happens after I apply?',
  description: 'what happens after application title text',
});
const whyLinkedInAccountText = defineMessage({
  defaultMessage: 'Why do I need to apply with my LinkedIn account?',
  description: 'why do i need a linkedin account title text',
});
const whatIfNoLinkedIn = defineMessage({
  defaultMessage: 'What if I can’t access my LinkedIn account or don’t have an existing LinkedIn account?',
  description: 'what if no linkedin available title text',
});

const pageTitle = defineMessage({
  defaultMessage: 'Join',
  description: 'team member join page title',
});
const title = defineMessage({
  defaultMessage: "We're excited you're here",
  description: 'Join page title',
});
const description = defineMessage({
  defaultMessage:
    'We believe in the power of building your network while building your business, which is why we require all Microsoft for Startups users to have a LinkedIn account. Once you join, we will use your LinkedIn profile to help you grow and activate your network. Please keep in mind that you can change your account syncing at any time via your LinkedIn profile settings.',
  description: 'Join page description',
});
const successLabel = defineMessage({
  defaultMessage: 'Confirmed email address',
  description: 'success message for email address confirmation',
});

const joinTitle = defineMessage({
  defaultMessage: 'Join your teammates in the Microsoft for Startups Founder Hub',
  description: 'join page main title',
});
const joinDescription = defineMessage({
  defaultMessage:
    'Our team of startup mentors, technical advisors, and all sorts of other good stuff are waiting for you!',
  description: 'join page main description',
});

const confirmEmailLabel = defineMessage({
  defaultMessage: 'Confirm email',
  description: 'confirm email address field label',
});
const verifyEmailLabel = defineMessage({
  defaultMessage: 'Verify',
  description: 'button label to verify email address',
});

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    ...shorthands.overflow('hidden'),
    minHeight: '100vh',
    '@media screen and (max-width: 768px)': {
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  },
  accordion: {
    color: tokens.colorNeutralForegroundInverted,
  },
  asideImage: {
    height: '300px',
    aspectRatio: 0.5,
    width: '100%',
    maxWidth: '600px',
    alignSelf: 'center',
    justifySelf: 'flex-end',
    marginTop: 'auto',
  },
  content: {
    ...shorthands.padding(tokens.spacingHorizontalXXL),
    boxSizing: 'border-box',
    flexBasis: '50%',
    flexGrow: 1,
    flexShrink: 1,
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingHorizontalL),
    width: '100%',
    overflowY: 'auto',
    '@media screen and (max-width: 768px)': {
      ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    },
  },

  aside: {
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.padding(tokens.spacingHorizontalXXL),
    flexBasis: '50%',
    boxSizing: 'border-box',
    flexGrow: 1,
    color: tokens.colorNeutralForegroundOnBrand,
    '& a': {
      color: tokens.colorNeutralForegroundInvertedLink,
      textDecorationLine: 'underline',
      ':hover': {
        color: tokens.colorNeutralForegroundInvertedLinkHover,
      },
    },
  },
  asideContent: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'stretch',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.gap(tokens.spacingVerticalXXL),
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    rowGap: tokens.spacingVerticalM,
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    columnGap: tokens.spacingHorizontalM,
  },
});
